// src/services/counselingApi.js
import axios from 'axios';

const BASE_URL = process.env.REACT_APP_API_URL1 || 'http://localhost:5001';
const API_URL = BASE_URL + '/api/v1';

console.log("✔ Counseling API Base URL:", API_URL);

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Helper to get cookie
function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
}

// Helper to get token from multiple sources
function getAuthToken() {
  return localStorage.getItem('token') || 
         localStorage.getItem('auth_token') || 
         getCookie('auth_token') ||
         getCookie('token');
}

// Request interceptor - Add auth token
api.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    
    if (token) {
      config.headers.Authorization = token.startsWith('Bearer ') 
        ? token 
        : `Bearer ${token}`;
    }
    
    console.log('📤 API Request:', config.method.toUpperCase(), config.url);
    
    return config;
  },
  (error) => {
    console.error('❌ Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor - Handle unauthorized
api.interceptors.response.use(
  (response) => {
    console.log('📥 API Response:', response.config.url, '-', response.status);
    return response;
  },
  (error) => {
    console.error('❌ API Error:', error.config?.url, '-', error.response?.status);
    console.error('❌ Error Details:', error.response?.data);
    
    if (error.response?.status === 401) {
      if (!window.location.pathname.includes('/login')) {
        console.log('🔒 Unauthorized - Clearing auth and redirecting');
        localStorage.clear();
        document.cookie.split(";").forEach((c) => {
          document.cookie = c
            .replace(/^ +/, "")
            .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
        });
        window.location.href = '/login?type=partner';
      }
    }
    
    return Promise.reject(error);
  }
);

// ==================== RAZORPAY SCRIPT LOADER (NEW) ====================

const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    // Check if already loaded
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    // Load script dynamically
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => {
      console.log('✅ Razorpay SDK Loaded via Script Tag');
      resolve(true);
    };
    script.onerror = () => {
      console.error('❌ Failed to load Razorpay SDK');
      resolve(false);
    };
    document.body.appendChild(script);
  });
};

// ==================== COUNSELOR REGISTRATION & PAYMENT ====================

export const counselorRegistrationApi = {
  // Get pricing information (PUBLIC - no auth required)
  getPricing: async () => {
    try {
      const response = await api.get('/counselors/registration/pricing');
      return response.data;
    } catch (error) {
      console.error('❌ Failed to get pricing:', error);
      throw error;
    }
  },

  // Validate Coupon (Hits the Counselor specific route)
  validateCoupon: async (couponCode, orderAmount) => {
    try {
      console.log('🎟️ Validating coupon:', couponCode);
      const response = await api.post('/counselors/registration/validate-coupon', {
        couponCode,
        orderAmount: parseFloat(orderAmount)
      });
      console.log('✅ Coupon valid:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Coupon validation failed:', error.response?.data || error.message);
      throw error;
    }
  },

  // Save profile draft before payment (PROTECTED - requires auth)
  saveDraft: async (profileData) => {
    try {
      console.log('💾 Saving draft...');
      const response = await api.post('/counselors/registration/draft', profileData);
      console.log('✅ Draft saved successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Failed to save draft:', error.response?.data || error.message);
      throw error;
    }
  },

  // Create payment order (PROTECTED - requires auth)
  createPaymentOrder: async (orderData = {}) => {
    try {
      console.log('💳 Creating payment order:', orderData);
      const response = await api.post('/counselors/registration/order', orderData);
      console.log('✅ Payment order created:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Failed to create payment order:', error.response?.data || error.message);
      throw error;
    }
  },

  // Verify payment and submit for approval (PROTECTED - requires auth)
  verifyPayment: async (paymentData) => {
    try {
      console.log('✔️ Verifying payment:', paymentData);
      const response = await api.post('/counselors/registration/verify-payment', paymentData);
      console.log('✅ Payment verified:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Payment verification failed:', error.response?.data || error.message);
      throw error;
    }
  },
};

// ==================== COUNSELOR PROFILE & DASHBOARD ====================

export const counselingApi = {
  // -------------- PROFILE -------------------
  getCounselorProfile: async () => {
    try {
      const response = await api.get('/counselors/profile');
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },

  updateCounselorProfile: async (data) => {
    try {
      const response = await api.put('/counselors/profile', data);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  uploadCounselorPhoto: async (file) => {
    try {
      const formData = new FormData();
      formData.append('photo', file);
      const response = await api.post('/counselors/profile/photo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // -------------- AVAILABILITY --------------
  updateAvailability: async (slots) => {
    try {
      const response = await api.put('/counselors/availability', {
        available_slots: slots,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getAvailability: async () => {
    try {
      const response = await api.get('/counselors/availability');
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        return { available_slots: [] };
      }
      throw error;
    }
  },

  toggleAcceptingBookings: async (accepting) => {
    try {
      const response = await api.put('/counselors/toggle-accepting-bookings', {
        accepting_bookings: accepting,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getAcceptingStatus: async () => {
    try {
      const response = await api.get('/counselors/accepting-status');
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        return { accepting_bookings: false };
      }
      throw error;
    }
  },

  // -------------- PUBLICATION --------------
  getPublicationStatus: async () => {
    try {
      const response = await api.get('/counselors/publication-status');
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        return { data: { publication_status: 'not_requested' } };
      }
      throw error;
    }
  },

  requestPublication: async (data) => {
    try {
      const response = await api.post('/counselors/request-publication', data);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // -------------- BOOKINGS -------------------
  getCounselorBookings: async (status = '') => {
    try {
      const params = status ? `?status=${status}` : '';
      const response = await api.get(`/counselors/bookings${params}`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        return { bookings: [] };
      }
      throw error;
    }
  },

  updateBookingStatus: async (bookingId, data) => {
    try {
      const response = await api.put(`/counselors/bookings/${bookingId}/status`, data);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // -------------- BLOGS ----------------------
  getAllBlogs: async () => {
    try {
      const response = await api.get('/blogs');
      return response.data;
    } catch (error) {
      return { blogs: [] };
    }
  },

  getCounselorBlogs: async (id) => {
    try {
      const response = await api.get(`/blogs/counselor/${id}`);
      return response.data;
    } catch (error) {
      return { blogs: [] };
    }
  },

  createBlog: async (blogData) => {
    try {
      const response = await api.post('/blogs', blogData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  updateBlog: async (blogId, blogData) => {
    try {
      const response = await api.put(`/blogs/${blogId}`, blogData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  deleteBlog: async (blogId) => {
    try {
      const response = await api.delete(`/blogs/${blogId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};

// ==================== STUDENT BOOKING ====================

export const studentBookingApi = {
  getPublishedCounselors: async (filters = {}) => {
    try {
      const params = new URLSearchParams(filters);
      const response = await api.get(`/counselors/?${params}`);
      return response.data;
    } catch (error) {
      return { counselors: [] };
    }
  },

  getCounselorProfile: async (counselorId) => {
    try {
      const response = await api.get(`/counselors/${counselorId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getBookingPricing: async () => {
    try {
      const response = await api.get('/bookings/pricing');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  createBookingOrder: async (bookingData) => {
    try {
      const response = await api.post('/bookings/create-order', bookingData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  verifyBookingPayment: async (paymentData) => {
    try {
      const response = await api.post('/bookings/verify-payment', paymentData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getMyBookings: async () => {
    try {
      const response = await api.get('/bookings/my-bookings');
      return response.data;
    } catch (error) {
      return { bookings: [] };
    }
  },

  cancelBooking: async (bookingId) => {
    try {
      const response = await api.delete(`/bookings/${bookingId}/cancel`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};

// ==================== RAZORPAY PAYMENT HANDLERS ====================

export const handleRazorpayPayment = async (orderData, onSuccess, onFailure) => {
  console.log('💳 Initializing Razorpay with Data:', orderData);
  
  // 1. DYNAMICALLY LOAD SCRIPT
  const res = await loadRazorpayScript();
  if (!res) {
    onFailure('Razorpay SDK failed to load. Please check your internet connection.');
    return;
  }

  // 2. CHECK KEY ID
  if (!orderData.key_id) {
    console.error('❌ Missing Razorpay Key ID from backend');
    onFailure('System Error: Payment configuration missing. Please contact support.');
    return;
  }

  const options = {
    key: orderData.key_id,
    amount: orderData.amount,
    currency: orderData.currency || 'INR',
    order_id: orderData.order_id,
    name: 'Vidyazon',
    description: 'Counselor Registration Fee',
    image: '/logo.png',
    handler: function (response) {
      console.log('✅ Razorpay payment successful:', response);
      onSuccess({
        order_id: orderData.order_id,
        payment_id: response.razorpay_payment_id,
        signature: response.razorpay_signature,
      });
    },
    prefill: {
      name: localStorage.getItem('userName') || orderData.prefill?.name || '',
      email: localStorage.getItem('userEmail') || orderData.prefill?.email || '',
      contact: orderData.prefill?.contact || ''
    },
    theme: {
      color: '#667eea',
    },
    modal: {
      ondismiss: function () {
        console.warn('⚠️ Payment cancelled by user');
        onFailure('Payment cancelled by user');
      },
    },
  };

  try {
    const razorpay = new window.Razorpay(options);
    
    razorpay.on('payment.failed', function (response){
        console.error('❌ Razorpay Payment Failed:', response.error);
        onFailure(`Payment Failed: ${response.error.description}`);
    });

    razorpay.open();
  } catch (error) {
    console.error('❌ Failed to open Razorpay:', error);
    onFailure('Failed to initialize payment. Please try again.');
  }
};

export const handleBookingPayment = async (orderData, onSuccess, onFailure) => {
  console.log('💳 Initializing booking payment:', orderData);
  
  // 1. DYNAMICALLY LOAD SCRIPT
  const res = await loadRazorpayScript();
  if (!res) {
    onFailure('Razorpay SDK failed to load.');
    return;
  }

  // 2. CHECK KEY ID
  if (!orderData.key_id) {
    onFailure('System Error: Payment configuration missing.');
    return;
  }

  const options = {
    key: orderData.key_id,
    amount: orderData.amount,
    currency: orderData.currency || 'INR',
    order_id: orderData.order_id,
    name: 'Vidyazon',
    description: 'Counseling Session Booking',
    image: '/logo.png',
    handler: function (response) {
      console.log('✅ Booking payment successful:', response);
      onSuccess({
        order_id: orderData.order_id,
        payment_id: response.razorpay_payment_id,
        signature: response.razorpay_signature,
      });
    },
    prefill: {
      name: localStorage.getItem('userName') || '',
      email: localStorage.getItem('userEmail') || '',
    },
    theme: {
      color: '#667eea',
    },
    modal: {
      ondismiss: function () {
        onFailure('Payment cancelled by user');
      },
    },
  };

  try {
    const razorpay = new window.Razorpay(options);
    razorpay.open();
  } catch (error) {
    console.error('❌ Failed to open Razorpay:', error);
    onFailure('Failed to initialize payment. Please try again.');
  }
};

// Export everything
export default {
  counselorRegistrationApi,
  counselingApi,
  studentBookingApi,
  handleRazorpayPayment,
  handleBookingPayment,
};